import axios from "axios";
import type { AxiosRequestConfig } from "axios";

import { httpDelete, httpGet, httpPost } from "@/api/connection";
import {
  connectionFailureMessage,
  isConnectionFailure,
} from "@/api/connection.types";

import type {
  AssetAttachment,
  CreateAssetAttachmentBody,
  CreateAssetAttachmentResponse,
  DeleteAssetAttachmentResponse,
  GetAssetAttachmentsQuery,
  QueryAssetAttachmentsResult,
  UploadAssetAttachmentOptions,
} from "./asset-attachments.types";

// Shared trap for every wrapper here: connection.js resolves HTTP failures as
// { success: false, error } instead of rejecting (and toasts unless
// skipErrorToast), so callers must runtime-check the payload shape — see
// isConnectionFailure / connectionFailureMessage in connection.types.ts.
//
// There is no PATCH route: attachment metadata is immutable after create, so
// "replace" is delete + upload.

// GET /asset-attachments/:uuid returns the row even when uploaded = 0 (then
// file_url is null); 404 resolves as { success: false }.
function getAssetAttachments(
  query: GetAssetAttachmentsQuery & { uuid: string }
): Promise<AssetAttachment>;
// GET /asset-attachments. Strict query schema (see
// QueryAssetAttachmentsParams); only `uploaded = 1` rows come back.
function getAssetAttachments(
  query?: GetAssetAttachmentsQuery
): Promise<QueryAssetAttachmentsResult>;
async function getAssetAttachments(query: GetAssetAttachmentsQuery = {}) {
  const { uuid, skipErrorToast, ...params } = query;

  if (uuid) {
    return httpGet(`/asset-attachments/${uuid}`, {}, { skipErrorToast });
  }

  return httpGet(`/asset-attachments`, params, { skipErrorToast });
}

const getAssetAttachmentById = (
  uuid: string,
  options: { skipErrorToast?: boolean } = {}
): Promise<AssetAttachment> => getAssetAttachments({ uuid, ...options });

// POST /asset-attachments responds 201 with the row plus a one-hour presigned
// PUT `upload_url`. Prefer `upload`, which runs the whole flow.
async function createAssetAttachment(
  values: CreateAssetAttachmentBody,
  options: { skipErrorToast?: boolean } = {}
): Promise<CreateAssetAttachmentResponse> {
  return await httpPost(`/asset-attachments`, values, options);
}

// POST /asset-attachments/:uuid/complete takes no body. The api hashes the
// stored object, records its true size and flips uploaded to 1. Idempotent.
// 400 if the bytes were never PUT; 500 on a storage failure (not retryable).
async function completeAssetAttachment(
  uuid: string,
  options: { skipErrorToast?: boolean } = {}
): Promise<AssetAttachment> {
  return await httpPost(`/asset-attachments/${uuid}/complete`, {}, options);
}

// DELETE /asset-attachments/:uuid deletes the stored object first, then the
// row; a storage failure is a 500 and keeps the row.
async function deleteAssetAttachment(
  uuid: string,
  options: { skipErrorToast?: boolean } = {}
): Promise<DeleteAssetAttachmentResponse> {
  return await httpDelete(`/asset-attachments/${uuid}`, undefined, options);
}

/**
 * Create → PUT → complete in one call. Resolves to the completed record
 * (uploaded: 1, hashes and file_url set). Throws a plain Error naming the
 * step that failed; unlike the wrappers above it never resolves
 * { success: false }, so it drops straight into a react-query mutationFn.
 *
 * No cleanup on failure: a row whose bytes never land is swept server-side
 * after 24h, and the complete step is idempotent, so a caller may simply
 * call again. A 500 from complete is a storage failure and is not retried
 * here. The presigned URL is used as returned (no localhost:3000 → 3001
 * rewrite for the local file adapter); a consumer that needs one has to call
 * create / axios.put / complete itself.
 */
async function uploadAssetAttachment(
  file: File,
  {
    asset_uuid,
    validation_uuid,
    onUploadProgress,
    skipErrorToast,
  }: UploadAssetAttachmentOptions
): Promise<AssetAttachment> {
  // Leading dot (".bashrc") and trailing dot ("name.") both mean no ext.
  const dot = file.name.lastIndexOf(".");
  const ext =
    dot > 0 && dot < file.name.length - 1 ? file.name.slice(dot + 1) : null;

  const body: CreateAssetAttachmentBody = {
    asset_uuid,
    name: file.name,
    size: file.size,
    ext,
  };
  if (validation_uuid) body.validation_uuid = validation_uuid;

  const created = await createAssetAttachment(body, { skipErrorToast });
  if (isConnectionFailure(created)) {
    throw new Error(
      `Asset attachment create failed: ${connectionFailureMessage(created)}`
    );
  }
  if (!created?.upload_url) {
    throw new Error("Asset attachment create failed: no upload_url returned");
  }

  try {
    // Same call as api/evidence/photos.js: the signature covers key + expiry
    // only, so no headers are needed, and the local adapter streams the raw
    // body — never wrap the file in FormData.
    // skipErrorToast is read by the response interceptor in connection.js;
    // the app has no axios module augmentation, hence the cast.
    await axios.put(created.upload_url, file, {
      withCredentials: false,
      skipErrorToast,
      onUploadProgress: (progressEvent) => {
        if (onUploadProgress && progressEvent.total) {
          onUploadProgress(
            Math.round((progressEvent.loaded / progressEvent.total) * 100)
          );
        }
      },
    } as AxiosRequestConfig);
  } catch (error) {
    throw new Error(
      `Asset attachment upload failed: ${
        error instanceof Error ? error.message : String(error)
      }`
    );
  }

  const completed = await completeAssetAttachment(created.uuid, {
    skipErrorToast,
  });
  if (isConnectionFailure(completed)) {
    throw new Error(
      `Asset attachment complete failed: ${connectionFailureMessage(completed)}`
    );
  }
  return completed;
}

export const AssetAttachmentsAPI = {
  get: getAssetAttachments,
  getById: getAssetAttachmentById,
  create: createAssetAttachment,
  complete: completeAssetAttachment,
  delete: deleteAssetAttachment,
  upload: uploadAssetAttachment,
};
